import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'

import { PageHeader } from '@/components/headers/PageHeader'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { getOrganizationOptions } from '@/utils/helpers'
import { fetchVoiceAgents } from '@/reducers/agentProvisioning/agentProvisioning.actions'

import { buildFullName, resolveAgentName } from './agentNameHelpers'
import AgentCard from './components/AgentCard'
import './VoiceAgentsListPage.scss'

const VoiceAgentsListPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  // Both presentations are sourced from GET /voice-agents — every provisioned
  // agent in the org, regardless of whether its prompt config (or code) exists
  // yet.
  // As of AVR-556 this response also carries the card fields (title, purpose,
  // direction, created_at, campaign_count) derived server-side from the agent's
  // prompt config + campaigns — so the grid needs no second request.
  const provisioningAgents = useSelector((state) => state.agentProvisioning.agents.items)
  const loading = useSelector((state) => Boolean(state.agentProvisioning.loading.agents))
  const organizations = useSelector((state) => state.organizations)
  const currentUser = useSelector((state) => state.currentUser)
  const isBaltoAdmin = currentUser?.organizationid === 1
  // Who can open an agent's editor: admins and Togo editors (the same access the
  // `/voice-agents/*` routes gate on). Drives the clickable rows + link styling.
  const canEditAgents = isBaltoAdmin || Boolean(currentUser?.togo_editor)

  // Local-only org switching for Balto admins. Non-admins use their own org
  // (the default fetchingAPI header). Default to the user's current org so the
  // list loads on first visit; `?org=` (threaded in after creating an agent, or
  // by the agent header's back link) takes precedence.
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    const parsed = queryString.parse(location.search)
    if (parsed.org) {
      return Number(parsed.org)
    }
    return currentUser?.organizationid ?? null
  })

  useEffect(() => {
    // Fetch on arrival for the defaulted org; re-fetch when the picker changes.
    // An admin who clears the picker sees the "Select an organization" prompt —
    // skip the fetch rather than needlessly reloading their own org's agents.
    if (isBaltoAdmin && !selectedOrgId) {
      return
    }
    // Non-admins omit the org param (BE defaults to the caller's org).
    dispatch(fetchVoiceAgents(isBaltoAdmin ? selectedOrgId : null))
  }, [dispatch, isBaltoAdmin, selectedOrgId])

  const organizationOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])

  // If an admin clears the picker there's no org to show — gating here avoids
  // surfacing a stale Redux list and drives the "Select an organization" prompt.
  const showAgents = !isBaltoAdmin || Boolean(selectedOrgId)

  const rows = useMemo(() => {
    if (!showAgents) {
      return []
    }
    return (
      provisioningAgents
        .map((agent) => {
          const hasConfig = !isEmpty(agent.configs)
          const cartesiaAgentId = agent.configs?.[0]?.agent_id
          const fullName = buildFullName(agent.owner_first_name, agent.owner_last_name)
          return {
            id: agent.id,
            slug: agent.slug,
            userId: agent.user_id,
            fullName,
            useCaseName: agent.use_case_name || '',
            agentId: cartesiaAgentId,
            // Provisioned = finished the wizard, so it has a config to open.
            provisioned: hasConfig,
            // --- card-only fields (unused by the admin table) ---
            // The agent's name, resolved the same way the header resolves it so
            // one rename shows up in both (AVR-827). The owner's name leads
            // because that is the half a rename writes; the config title is the
            // fallback for an agent whose user has no usable name.
            name: resolveAgentName(agent),
            // The externalized purpose is a real sentence about what the agent
            // does; the use-case name is the terse stand-in when there is none.
            description: agent.purpose || agent.use_case_name || '',
            // Null when the agent's config never declared a direction — the BE
            // deliberately doesn't default it to "inbound", so the card shows no
            // pill rather than asserting a direction nobody set.
            direction: agent.direction || null,
            // Last change to the agent's prompt config, relative ("3 days ago").
            // The BE sends a raw timestamp so the formatting choice lives here.
            // Null for an agent with no config.
            lastUpdated: agent.updated_at ? moment.utc(agent.updated_at).fromNow() : null,
            // Only meaningful for outbound agents; the card hides it otherwise
            // (AVR-556: inbound shows "Inbound" alone, with no count).
            campaignCount: agent.campaign_count ?? 0,
            // Keep the orb stable per agent across renders and org switches.
            orbKey: agent.slug || String(agent.id),
          }
        })
        // List every provisioned agent in the org — for customers and admins
        // alike — so they're clickable. Access to each agent is enforced
        // server-side (org scoping on the list, plus the per-agent ownership
        // guard on open/edit), so there's no need to hide not-yet-deployed ones.
        .filter((row) => row.provisioned)
        // Sort by the label each card actually shows, so the grid reads
        // alphabetically. The admin table re-sorts itself by `fullName` from
        // `defaultOrderBy`, so this ordering only surfaces in the grid.
        .sort((first, second) => first.name.localeCompare(second.name))
    )
  }, [provisioningAgents, showAgents])

  // Mirror the picked org into `?org=` so it survives navigation into an agent
  // and back (the agent header's back link preserves the query) — returning to
  // the list restores the org and its already-loaded agents instead of resetting
  // to the "Select an organization" prompt.
  const handleOrgSelect = (option, action) => {
    const nextOrgId = action?.action === 'clear' ? null : option.value
    setSelectedOrgId(nextOrgId)
    const next = { ...queryString.parse(location.search) }
    if (nextOrgId) {
      next.org = nextOrgId
    } else {
      delete next.org
    }
    history.replace({ pathname: location.pathname, search: queryString.stringify(next) })
  }

  // Open the agent's page (keyed by the agent's user). Thread the selected org so
  // the agent header's back link can return to the same scoped list.
  const buildAgentPath = (userId) => {
    const search = selectedOrgId ? `?${queryString.stringify({ org: selectedOrgId })}` : ''
    return `/voice-agents/by-user/${userId}${search}`
  }

  // Only the agent's name opens the editor — render it as a real link so the
  // click target and the navigation affordance are the same element. Falls back
  // to plain text when the viewer can't edit.
  const renderNameLink = (value, row) =>
    canEditAgents ? (
      <Link className="table-link" to={buildAgentPath(row.userId)}>
        {value || '—'}
      </Link>
    ) : (
      value || '—'
    )

  // Show an em-dash rather than a blank cell (missing owner name, or agent_id on
  // a config-less agent).
  const renderDash = (value) => value || '—'

  // Table columns — admin-only now that customers get the card grid, so this is
  // the full cross-org picture (slug + Cartesia id) with no customer variant.
  const columns = [
    { accessor: 'fullName', label: 'Full name', isSearchable: true, format: renderNameLink },
    { accessor: 'agentId', label: 'Cartesia agent ID', format: renderDash },
    { accessor: 'slug', label: 'Slug', isSearchable: true, format: renderDash },
    { accessor: 'useCaseName', label: 'Use case', isSearchable: true, format: renderDash },
  ]

  const noDataHeader = isBaltoAdmin && !selectedOrgId ? 'Select an organization' : 'No AI agents'
  const noDataText =
    isBaltoAdmin && !selectedOrgId
      ? 'Choose an organization above to view its AI agents.'
      : 'No AI agents have been provisioned in this organization yet.'

  // Customer card grid. Loading and empty both render in a card-shaped surface
  // so the page keeps its footprint between states.
  const renderAgentGrid = () => {
    if (loading && isEmpty(rows)) {
      return (
        <div className="voice-agents-list__placeholder" data-testid="voice-agents-grid-loading">
          <Loader centered label="Loading AI agents" />
        </div>
      )
    }

    if (isEmpty(rows)) {
      return (
        <div className="voice-agents-list__placeholder">
          <NoData headerText={noDataHeader} text={noDataText} />
        </div>
      )
    }

    return (
      <div className="voice-agents-list__grid" data-testid="voice-agents-grid">
        {rows.map((row) => (
          <AgentCard
            key={row.id}
            agent={row}
            to={canEditAgents ? buildAgentPath(row.userId) : null}
          />
        ))}
      </div>
    )
  }

  return (
    <div data-testid="voice-agents-list-page">
      <PageHeader
        title="Agent Studio"
        actions={
          // Provisioning a new agent is a Balto-admin (org 1) operation; regular
          // org users manage only their existing agents, so hide the CTA for them.
          isBaltoAdmin ? (
            <Button brand onClick={() => history.push('/voice-agents/create')}>
              Create agent
            </Button>
          ) : null
        }
      />

      {isBaltoAdmin && (
        <FilterForm small>
          <OrganizationFilter
            options={organizationOptions}
            value={selectedOrgId}
            onChange={handleOrgSelect}
            isClearable
          />
        </FilterForm>
      )}

      {/* Customers get the card grid (AVR-556); org 1 keeps the cross-org table,
          whose slug / Cartesia-id columns and org picker are admin tooling the
          cards deliberately don't carry. */}
      {isBaltoAdmin ? (
        <Card className="no-padding">
          <AdvancedTable
            testid="voice-agents-table"
            loading={loading}
            rows={rows}
            columns={columns}
            defaultOrderBy="fullName"
            defaultOrder="asc"
            pagination
            noDataHeader={noDataHeader}
            noDataText={noDataText}
          />
        </Card>
      ) : (
        renderAgentGrid()
      )}
    </div>
  )
}

export default VoiceAgentsListPage
